<template>
  <div>
    <aside
      v-if="showOffer"
      class="pwa-install-offer elevation-5"
      aria-label="Install Self Biography"
    >
      <img
        src="/icons/app-icon.png"
        alt=""
        width="44"
        height="44"
      />
      <div class="pwa-offer-copy">
        <strong>Keep your story close</strong>
        <p>Install Self Biography for easy access from your {{ mobile ? 'home screen' : 'desktop' }}.</p>
        <div class="pwa-offer-actions">
          <v-btn
            small
            color="primary"
            @click="openInstallDialog"
          >
            Install app
          </v-btn>
          <v-btn
            small
            text
            @click="dismissInstallOffer"
          >
            Not now
          </v-btn>
        </div>
      </div>
    </aside>

    <v-dialog
      v-model="pwa.dialog"
      max-width="480"
      scrollable
      aria-labelledby="pwa-install-title"
    >
      <v-card>
        <v-card-title
          id="pwa-install-title"
          class="pwa-title"
        >
          Install Self Biography
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close installation instructions"
            @click="close"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pwa-install-details">
          <p v-if="pwa.message" role="status">{{ pwa.message }}</p>
          <template v-if="pwa.nativePrompt && pwa.secure && !pwa.installed">
            <p>Click <strong>Install now</strong>, then confirm in the window opened by your browser. After installation, open Self Biography from its app icon.</p>
            <v-btn color="primary" :loading="pwa.busy" @click="requestInstall">
              Install now
            </v-btn>
          </template>
          <template v-else>
            <p v-if="pwa.embedded">Open this page in Safari or Chrome.</p>
            <template v-if="pwa.platform === 'ios'">
              <h3>iPhone / iPad</h3>
              <ol>
                <li>Open Self Biography in <strong>Safari</strong>. Tap <strong>Share</strong>, the square with an upward arrow. It may be inside the page menu.</li>
                <li>Scroll through the sharing options and tap <strong>Add to Home Screen</strong>.</li>
                <li>Keep <strong>Open as Web App</strong> enabled if shown, then tap <strong>Add</strong> to finish.</li>
                <li>Go to your home screen and tap the <strong>Self Biography</strong> icon to open the app.</li>
              </ol>
            </template>
            <template v-else-if="pwa.platform === 'android'">
              <h3>Android</h3>
              <ol>
                <li>Open Self Biography in <strong>Chrome</strong> and tap the three-dot menu <strong>⋮</strong> at the top right.</li>
                <li>Select <strong>Install app</strong> or <strong>Add to Home screen</strong> from the menu.</li>
                <li>Confirm by tapping <strong>Install</strong> or <strong>Add</strong>, then open Self Biography from its icon on your home screen or in your app list.</li>
              </ol>
            </template>
            <template v-else-if="pwa.platform === 'mac-safari'">
              <h3>Safari on Mac</h3>
              <ol>
                <li>With Self Biography open in <strong>Safari</strong>, choose <strong>File → Add to Dock</strong> from the menu bar at the top of the screen.</li>
                <li>Keep the name <strong>Self Biography</strong> and click <strong>Add</strong>.</li>
                <li>Click the new Self Biography icon in your <strong>Dock</strong> to open the app.</li>
              </ol>
            </template>
            <template v-else>
              <h3>Chrome / Edge</h3>
              <ol>
                <li>Open Self Biography in <strong>Chrome</strong> or <strong>Edge</strong>. Click the install icon on the right side of the address bar, or find <strong>Install app</strong> in the browser menu.</li>
                <li>Click <strong>Install</strong> in the confirmation window.</li>
                <li>Open Self Biography from the new app shortcut on your computer.</li>
              </ol>
            </template>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="close"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiClose } from '@mdi/js'
import {
  pwaState, canOfferInstall, openInstallDialog, dismissInstallOffer, requestInstall,
} from '@/services/pwa'

export default {
  data: () => ({
    pwa: pwaState, mdiClose, offerReady: false, offerTimer: null,
  }),
  computed: {
    mobile() { return this.$vuetify.breakpoint.smAndDown },
    eligible() {
      const { state } = this.$store

      return canOfferInstall() && state.logged && !this.pwa.dialog
        && Date.now() >= this.pwa.dismissedUntil && this.$route.meta.auth
        && !state.agreements && !state.modal_upload && !state.modal_edit && !state.diary && !state.gallery.open
    },
    showOffer() { return this.offerReady && this.eligible },
  },
  watch: {
    'pwa.dialog': function dialogChanged(value, previous) {
      if (!value && previous && !this.pwa.installed) dismissInstallOffer()
    },
    eligible: {
      immediate: true,
      handler(value) {
        clearTimeout(this.offerTimer)
        this.offerReady = false
        if (value) this.offerTimer = setTimeout(() => { this.offerReady = true }, this.mobile ? 15000 : 45000)
      },
    },
  },
  beforeDestroy() { clearTimeout(this.offerTimer) },
  methods: {
    openInstallDialog,
    dismissInstallOffer,
    requestInstall,
    close() { this.pwa.dialog = false; dismissInstallOffer() },
  },
}
</script>

<style scoped lang="scss">
.pwa-install-offer {
  position: fixed;
  z-index: 8;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: 20px;
  width: min(380px, calc(100vw - 40px));
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: var(--v-background-base, #fff);
  color: #42394d;
  border: 1px solid rgba(145, 85, 253, 0.25);
}
.pwa-install-offer img { border-radius: 8px; flex-shrink: 0; }
.pwa-offer-copy { min-width: 0; }
.pwa-offer-copy p { font-size: 13px; line-height: 1.5; margin: 4px 0 12px; }
.pwa-offer-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.pwa-title { flex-wrap: nowrap; font-size: 20px; word-break: normal; }
.pwa-install-details h3 { font-size: 16px; margin-bottom: 12px; }
.pwa-install-details li { margin-bottom: 12px; }
.pwa-install-details .v-btn { max-width: 100%; height: auto; min-height: 36px; white-space: normal; }
.pwa-install-details ::v-deep .v-btn__content { flex: 1 1 auto; }
</style>
